import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "./Button";
import { MicroLabel } from "./Text";

/**
 * Putting a string on the clipboard, and saying whether it got there.
 *
 * ## Why this exists as a component
 *
 * Three panels now hand an operator a value to paste somewhere else — a station
 * credential, an OCPP URL, a driver-facing address — and each one had, or would
 * have had, its own `navigator.clipboard.writeText` with its own idea of what to do
 * when the browser refuses. It refuses more often than it looks: the API is gated on
 * a secure context, so a panel reached over plain http on a depot LAN has no
 * clipboard at all.
 *
 * ## The button never claims a success it did not have
 *
 * On refusal the label does not change and the value stays selectable and on screen.
 * That is the whole failure plan: every caller renders the string, so an operator
 * whose browser will not copy can still read it or select it by hand — and a button
 * that said *Copied* when nothing was copied would send them to paste an empty
 * clipboard into a charger's configuration screen.
 */

/** Long enough to be read, short enough that the button is ready for the next value. */
const CONFIRMATION_MS = 2500;

/**
 * One attempt at the clipboard, and the label state that follows it.
 *
 * Shared with `ui/QrCode`'s image copy, which writes a PNG rather than a string but
 * owes the operator exactly the same two promises: confirm only what actually
 * reached the clipboard, and stay quiet — not broken — when the browser refuses.
 * Two copies of that rule are two chances for one of them to start claiming success
 * it did not have.
 */
export function useCopyAttempt(write: () => Promise<unknown>): {
  readonly isCopied: boolean;
  readonly attempt: () => void;
} {
  const [isCopied, setIsCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // An operator can copy the URL and then the credential inside the confirmation
  // window; without this the second press would be cleared by the first timer.
  useEffect(() => () => clearTimeout(timer.current), []);

  const attempt = useCallback(() => {
    void (async () => {
      try {
        await write();
      } catch {
        // Refused: no secure context, or policy. Nothing to report — the value is on
        // screen either way, and the label simply does not claim otherwise.
        return;
      }

      setIsCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setIsCopied(false), CONFIRMATION_MS);
    })();
  }, [write]);

  return { isCopied, attempt };
}

export interface CopyButtonProps {
  readonly value: string;
  /** What is being copied, for the accessible name — "Copy the OCPP URL". */
  readonly what: string;
  readonly variant?: "primary" | "secondary" | "ghost";
  readonly children?: string;
}

export function CopyButton({
  value,
  what,
  variant = "secondary",
  children = "Copy",
}: CopyButtonProps) {
  const write = useCallback(() => navigator.clipboard.writeText(value), [value]);
  const { isCopied, attempt } = useCopyAttempt(write);

  return (
    <Button variant={variant} aria-label={`${what}: copy`} onClick={attempt}>
      {isCopied ? "Copied" : children}
    </Button>
  );
}

export interface CopyableProps {
  readonly label: string;
  readonly value: string;
  /**
   * Wrapped rather than truncated. These are URLs an operator reads to check they
   * are pointed at the right host, and an ellipsis hides exactly the part they are
   * checking.
   */
  readonly hint?: string;
}

/**
 * A labelled, monospaced, selectable value with a copy button beside it. The unit
 * every "here is a string, take it elsewhere" panel is built from.
 */
export function Copyable({ label, value, hint }: CopyableProps) {
  return (
    <div className="flex flex-col gap-1">
      <MicroLabel>{label}</MicroLabel>
      <div className="flex flex-wrap items-start gap-3">
        {/*
          `select-all` so a triple click takes the whole value and not the word the
          pointer landed on, and `break-all` because these are long strings with no
          break opportunities a browser recognises.
        */}
        <code className="min-w-0 flex-1 select-all break-all bg-paper px-2 py-1 font-mono text-data text-ink">
          {value}
        </code>
        <CopyButton value={value} what={label} />
      </div>
      {hint === undefined ? null : (
        <span className="text-micro tracking-normal normal-case text-ink-faint">{hint}</span>
      )}
    </div>
  );
}
